import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useMyRefCode from "@/hooks/useMyRefCode";

/** Data + actions for the /c/:slug page — keeps CoinDetail.jsx a thin renderer. */
export default function useCoinDetail(slug) {
  const [coin, setCoin] = useState(null);
  const [price, setPrice] = useState(null);
  const [chart, setChart] = useState([]);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/coins/${slug}`);
      setCoin(data);
      setLikes(data.likes || 0);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
    }
  }, [slug]);

  useEffect(() => {
    load();
    setLiked(localStorage.getItem(`liked_${slug}`) === "1");
  }, [load, slug]);

  useEffect(() => {
    if (!slug) return;
    const fetchPrice = async () => {
      try {
        const [p, c] = await Promise.all([
          api.get(`/coins/${slug}/price`),
          api.get(`/coins/${slug}/chart?hours=24`),
        ]);
        setPrice(p.data);
        setChart(
          c.data.points.map((pt) => ({
            t: new Date(pt.t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            p: pt.p,
          }))
        );
      } catch {
        // Intentional: this auto-polls every 15s; transient network blips
        // should not flash an error UI. Next tick will recover.
      }
    };
    fetchPrice();
    const i = setInterval(fetchPrice, 15000);
    return () => clearInterval(i);
  }, [slug]);

  const like = async () => {
    if (liked) return;
    try {
      const { data } = await api.post(`/coins/${slug}/like`);
      setLikes(data.likes ?? likes + 1);
      setLiked(true);
      localStorage.setItem(`liked_${slug}`, "1");
    } catch (e) {
      if (e.response?.status === 429) toast.error("Slow down 🤚");
    }
  };

  const myRef = useMyRefCode();
  const share = async () => {
    const u = new URL(window.location.href);
    if (myRef) u.searchParams.set("ref", myRef);
    const url = u.toString();
    if (navigator.share) {
      navigator.share({ title: coin?.name, url }).catch(() => {});
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      } catch {
        toast.error("Copy failed — long-press the URL to copy manually");
      }
    }
  };

  return { coin, price, chart, likes, liked, notFound, load, like, share };
}
